import React from 'react';
import { Download, FileText, Receipt, Image as ImageIcon } from 'lucide-react';

/**
 * Customer-facing Documents Hub for an order — iter111.
 *
 * Replaces the previous flat row of underline-style download links
 * (proof / quote / order confirmation) with a single consolidated
 * grid of document cards. Each card shows an icon, label, one-line
 * description, and a primary "Download" CTA.
 *
 * Why a dedicated component? It centralises the visibility rules
 * (proof requires artwork; order-confirmation hidden for drafts) and
 * gives the customer a clean inventory of every artefact tied to
 * their order — no more "where do I find my invoice?" tickets.
 *
 * Props:
 *   order                       — full enhanced_orders document
 *   onDownloadProof(id, number)
 *   onDownloadQuote(id, number)
 *   onDownloadOrderConfirmation(id, number)
 *
 * iter114s.79 — Removed the "Cut File (SVG)" tile per the operator's
 * request; that artefact is no longer a customer-facing deliverable.
 */

const DOC_CARD_BASE = 'bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-2 hover:border-[#6FBF73] hover:shadow-sm transition-all';

const DocumentCard = ({ icon: Icon, accent, label, description, onDownload, testId, disabled }) => (
  <div className={DOC_CARD_BASE} data-testid={testId}>
    <div className="flex items-start gap-2">
      <div
        className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${accent}`}
        aria-hidden
      >
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-800 leading-tight">{label}</p>
        <p className="text-xs text-gray-500 leading-snug">{description}</p>
      </div>
    </div>
    <button
      type="button"
      onClick={onDownload}
      disabled={disabled}
      data-testid={`${testId}-btn`}
      className="mt-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[#4A7C59] hover:text-[#3D6A4A] disabled:text-gray-400 disabled:cursor-not-allowed"
    >
      <Download className="w-3.5 h-3.5" />
      Download
    </button>
  </div>
);

export const DocumentsHub = ({
  order,
  onDownloadProof,
  onDownloadQuote,
  onDownloadOrderConfirmation,
}) => {
  const hasArtwork =
    (order.artwork_files?.length || 0) > 0 ||
    (order.items || []).some((it) => (it.artwork_file_ids || []).length > 0);

  // Order-confirmation only makes sense once the order has been
  // submitted — drafts haven't generated one yet.
  const isDraftLike = ['draft', 'draft_with_artwork', ''].includes(
    (order.status || '').toLowerCase(),
  );

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm" data-testid={`documents-hub-${order.id}`}>
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <FileText className="w-5 h-5 text-[#4A7C59]" />
        Documents
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <DocumentCard
          icon={FileText}
          accent="bg-slate-600"
          label="Quote PDF"
          description="Branded quote — line items, pricing, applied discounts."
          onDownload={() => onDownloadQuote(order.id, order.order_number)}
          testId={`docs-quote-${order.id}`}
        />
        {!isDraftLike && (
          <DocumentCard
            icon={Receipt}
            accent="bg-amber-700"
            label="Order Confirmation PDF"
            description="Line items, shipping, tax, estimated ship date."
            onDownload={() => onDownloadOrderConfirmation(order.id, order.order_number)}
            testId={`docs-order-confirmation-${order.id}`}
          />
        )}
        {hasArtwork && (
          <DocumentCard
            icon={ImageIcon}
            accent="bg-[#4A7C59]"
            label="Proof PDF"
            description="What we'll print — exact artwork laid out at production size."
            onDownload={() => onDownloadProof(order.id, order.order_number)}
            testId={`docs-proof-${order.id}`}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentsHub;
